import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { loginErrorMessage, safeRedirect } from '../features/auth/login-feedback';
import { login, resetSessionCache, sessionQueryOptions } from '../features/auth/session';

const searchSchema = z.object({
  redirect: z.string().optional(),
});

const credentialsSchema = z.object({
  username: z.string().trim().min(1, 'Scrie utilizatorul sau emailul.'),
  password: z.string().min(1, 'Scrie parola.'),
});

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  beforeLoad: async ({ context, search }) => {
    // Cine e deja logat n-are ce căuta pe formular: ar putea deschide o a doua sesiune
    // fără s-o închidă pe prima.
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions());

    if (session) {
      throw redirect({ to: safeRedirect(search.redirect) ?? '/' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: async (session) => {
      resetSessionCache(queryClient, session);
      await navigate({ to: safeRedirect(search.redirect) ?? '/' });
    },
  });

  const form = useForm({
    defaultValues: { username: '', password: '' },
    validators: { onSubmit: credentialsSchema },
    onSubmit: ({ value }) => loginMutation.mutateAsync(value).then(() => undefined),
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-slate-900">
      <div className="w-full max-w-sm">
        <h1 className="mb-1 text-xl font-semibold">perfectCRM</h1>
        <p className="mb-6 text-sm text-slate-500">Autentificare operator</p>

        <form
          className="space-y-4 rounded-lg border border-slate-200 bg-white p-6"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.Field name="username">
            {(field) => (
              <div className="space-y-1">
                <label htmlFor={field.name} className="block text-sm text-slate-700">
                  Utilizator sau email
                </label>
                <input
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                  autoComplete="username"
                  // Prima acțiune a paginii e scrisul aici; fără asta ar cere un clic în plus.
                  // biome-ignore lint/a11y/noAutofocus: singurul câmp de start al ecranului
                  autoFocus
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
                />
                <FieldError messages={field.state.meta.errors} />
              </div>
            )}
          </form.Field>

          <form.Field name="password">
            {(field) => (
              <div className="space-y-1">
                <label htmlFor={field.name} className="block text-sm text-slate-700">
                  Parolă
                </label>
                <input
                  id={field.name}
                  name={field.name}
                  type="password"
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                  autoComplete="current-password"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
                />
                <FieldError messages={field.state.meta.errors} />
              </div>
            )}
          </form.Field>

          {loginMutation.isError ? (
            <p role="alert" className="text-sm text-red-700">
              {loginErrorMessage(loginMutation.error)}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {loginMutation.isPending ? 'Se verifică…' : 'Intră în cont'}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-slate-500">
          Nu există înregistrare. Conturile se creează de un administrator.
        </p>
      </div>
    </div>
  );
}

/** Erorile de câmp vin de la Zod ca obiecte `StandardSchemaV1.Issue`, nu ca text. */
function FieldError({ messages }: { messages: ReadonlyArray<{ message?: string } | undefined> }) {
  const first = messages.find((issue) => issue?.message);

  if (!first?.message) {
    return null;
  }

  return <p className="text-xs text-red-700">{first.message}</p>;
}
